import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Users, Target, Heart, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/product" className="text-slate-600 hover:text-slate-900">Product</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
            <Link to="/about" className="text-emerald-700 font-medium">About</Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900">Contact</Link>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login"><Button variant="ghost">Log in</Button></Link>
            <Link to="/signup"><Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full">Get Started</Button></Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </nav>
  );
};

const AboutPage = () => {
  const values = [
    {
      icon: Users,
      title: "Customer First",
      description: "Every feature we build starts with understanding what our customers need to succeed."
    },
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from code quality to customer support."
    },
    {
      icon: Heart,
      title: "Transparency",
      description: "We believe in honest, open communication with our team, customers, and community."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6" data-testid="about-title">
            About {process.env.REACT_APP_APP_NAME || 'Flowteras'}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300">
            We're on a mission to transform how businesses communicate with their customers through WhatsApp.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-6">Our Story</h2>
          <div className="prose prose-lg dark:prose-invert">
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {process.env.REACT_APP_APP_NAME || 'Flowteras'} was born from a simple observation: businesses were struggling to manage the growing volume of customer conversations on WhatsApp. Teams were drowning in messages, losing track of important conversations, and missing opportunities to delight customers.
            </p>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              We set out to build a platform that combines the power of AI with human expertise. {process.env.REACT_APP_APP_NAME || 'Flowteras'} helps businesses respond faster, automate repetitive tasks, and deliver exceptional customer experiences—all while keeping the personal touch that customers love.
            </p>
            <p className="text-slate-600 dark:text-slate-400">
              Today, Flowteras powers conversations for businesses across the globe, from small startups to enterprise organizations. But we're just getting started.
            </p>
            <p className="text-slate-600 dark:text-slate-400 mt-6" data-testid="about-entity-attribution">
              Flowteras is built and maintained by GREENMOUNT SOLUTIONS SDN. BHD., a Malaysia-based company.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-semibold text-center text-slate-900 dark:text-white mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <div key={i} className="text-center" data-testid={`about-value-${i}`}>
                <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{value.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-6">
            Join Us on This Journey
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8">
            Whether you're looking to improve customer conversations or join our team, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button className="btn-primary px-8 py-6">Get Started</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="px-8 py-6">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
